import { z } from 'zod/v3';
import { ok } from 'neverthrow';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
  ActionTrackMetadata,
} from '@cotera/client/v0/actions/framework';
import { AgentModel } from './agent.model';
import type { ActionTrackingContext } from '../actions/framework/action-tracking';
import { Schedule } from '@cotera/api';

type Params = {
  schedule?: Schedule;
};

export class ScheduleAgentAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Schedule Agent';
  description = 'Set the execution schedule for an agent';
  inputSchema = z.object({
    schedule: z.any().optional(), // Schedule type
  });
  discrete = true;

  applicable(context: ApplicableContext): boolean {
    return context.hasTargetedModelOfType(AgentModel);
  }

  async execute(
    params: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const agent = context.targetedModelOfType(AgentModel);

    if (agent === null) {
      return ok({});
    }

    if (params.schedule !== undefined) {
      // Update the schedule in the agent's form state
      agent.formState.set({
        ...agent.formState.snapshot(),
        schedule: params.schedule,
      });
    }

    return ok({});
  }

  track({
    payload,
    context,
  }: ActionTrackingContext<
    Params,
    { t: string; message?: string }[]
  >): ActionTrackMetadata | null {
    const agent = context.targetedModelOfType(AgentModel);
    if (agent === null) {
      return null;
    }

    const scheduleValue =
      typeof payload.schedule === 'string'
        ? payload.schedule
        : payload.schedule?.cron ?? 'unknown';

    return {
      event: 'agent_schedule_changed',
      properties: {
        agentId: agent.id,
        agentName: agent.name.snapshot(),
        schedule: scheduleValue,
        isNew: agent.isNew,
      },
    };
  }
}
